/**
 * Vertical Landing Page - For Real Estate Professionals
 * Same TripleTC product, targeted marketing
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Home, FileText, Camera, MessageSquare, TrendingUp, 
  CheckCircle, ArrowRight, Gift, Star, Building2
} from 'lucide-react';

const USE_CASES = [
  {
    icon: FileText,
    title: "Property Descriptions",
    description: "Turn basic listing notes into compelling, SEO-optimized descriptions that sell.",
    example: "3BR house with yard → Stunning family home with sun-drenched living spaces..."
  },
  {
    icon: Camera,
    title: "Virtual Staging Copy",
    description: "AI writes staging suggestions and describes empty rooms with furniture.",
    example: "Describe how this empty living room could be staged for young professionals"
  },
  {
    icon: MessageSquare,
    title: "Client Communications",
    description: "Draft follow-up emails, showing confirmations, and offer letters in seconds.",
    example: "Write a warm follow-up to clients who viewed 123 Main St yesterday"
  },
  {
    icon: TrendingUp,
    title: "Market Analysis",
    description: "AI researches comps, market trends, and creates CMA summaries.",
    example: "Research recent sales within 1 mile of 456 Oak Ave and summarize trends"
  },
  {
    icon: Building2,
    title: "Social Media Content",
    description: "Instagram captions, Facebook posts, and LinkedIn content for listings.",
    example: "Write 5 Instagram captions for this luxury penthouse listing"
  },
  {
    icon: Home,
    title: "Neighborhood Guides",
    description: "Auto-generate neighborhood descriptions with schools, dining, and amenities.",
    example: "Write a neighborhood guide for first-time buyers in downtown Austin"
  }
];

const TESTIMONIALS = [
  {
    quote: "I used to spend 45 minutes on each listing description. Now it takes 2 minutes.",
    author: "Sarah M.",
    role: "Realtor, Keller Williams",
    rating: 5
  },
  {
    quote: "The market analysis feature alone is worth 10x what I pay. Instant CMAs.",
    author: "Marcus T.",
    role: "Broker, RE/MAX",
    rating: 5
  },
  {
    quote: "My Instagram engagement doubled using AI-generated captions for listings.",
    author: "Jennifer L.",
    role: "Luxury Real Estate Agent",
    rating: 5
  }
];

export default function ForRealtors() {
  const navigate = useNavigate();
  const [hoveredCase, setHoveredCase] = useState(null);

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero */}
      <section className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-teal-500/10 border border-emerald-500/20 mb-8">
              <Home className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-medium text-emerald-300">
                Built for Real Estate Professionals
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Close More Deals.<br />
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                Write Less Copy.
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              AI that writes listing descriptions, market analyses, client emails, and social content. 
              <span className="text-emerald-400 font-semibold"> Your first 5 tasks are free.</span>
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/templates')}
                className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold rounded-full px-8 py-7 text-lg"
              >
                Try Free — No Credit Card
                <Gift className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById('use-cases')?.scrollIntoView({ behavior: 'smooth' })}
                className="border-zinc-700 text-white rounded-full px-8 py-7 text-lg"
              >
                See Use Cases
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>

            {/* Trust */}
            <div className="flex items-center justify-center gap-6 mt-10 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>NAR-friendly language</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>Fair housing compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                <span>MLS-ready output</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section id="use-cases" className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              What Realtors Use It For
            </h2>
            <p className="text-zinc-400">
              Every task that takes you away from clients — handled.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {USE_CASES.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                onMouseEnter={() => setHoveredCase(index)}
                onMouseLeave={() => setHoveredCase(null)}
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 hover:border-emerald-500/30 transition-all cursor-pointer"
              >
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{useCase.title}</h3>
                <p className="text-zinc-400 text-sm mb-4">{useCase.description}</p>
                
                {hoveredCase === index && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="bg-zinc-900/80 rounded-lg p-3 text-xs text-zinc-300 italic"
                  >
                    "{useCase.example}"
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Trusted by Top Producers
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-zinc-300 mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="text-white font-medium">{testimonial.author}</p>
                  <p className="text-zinc-500 text-sm">{testimonial.role}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-white mb-4">
              Simple Pricing. No Monthly Fees.
            </h2>
            <p className="text-zinc-400 mb-8">
              Pay only when you use it. Most listing descriptions cost less than a coffee.
            </p>

            <div className="bg-zinc-800/50 border border-zinc-700 rounded-2xl p-8 max-w-md mx-auto">
              <div className="text-5xl font-black text-white mb-2">$0.50</div>
              <div className="text-zinc-400 mb-6">per task</div>
              
              <ul className="text-left space-y-3 mb-8">
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  First 5 tasks FREE
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  No subscription required
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Unlimited revisions
                </li>
                <li className="flex items-center gap-3 text-zinc-300">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  Commercial use included
                </li>
              </ul>

              <Button
                size="lg"
                onClick={() => navigate('/templates')}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold rounded-full py-6 text-lg"
              >
                Start Free
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Your Competitors Are Already Using AI.
          </h2>
          <p className="text-zinc-400 mb-8">
            The question isn't whether to use AI. It's whether you'll be first or last.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/auth?redirect=/templates')}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-white font-bold rounded-full px-10 py-7 text-lg"
          >
            Get 5 Free Tasks Now
            <Gift className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
